import React, { useState, useRef } from "react";
import { Grid, Typography } from "@mui/material";
import { makeValidate, makeRequired } from "mui-rff";
import { Form } from "react-final-form";
import { AlertStateInterface } from "../../interfaces/AlertStateInterface";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getI18n, useTranslation } from "react-i18next";
import i18n from "i18next";
import ReCAPTCHA from "react-google-recaptcha";

import {
  PageAlert,
  PageLink,
  FormWrapperdiv,
  PageBox,
  FormButton,
  FormErrorHelperText,
  PageHeader,
  FormTextField
} from "./ForgotPassword.style";
import { ForgotPasswordSchema } from "../../schema/ForgotPasswordSchema";
import { ForgotPasswordInterface } from "../../interfaces/ForgotPasswordInterface";
import { useAuth } from "../../providers/Auth";
import { TabTitle } from "../../utils/GeneralFunctions";
/**
 * Uses the optional helper makeValidate function to format the error messages
 * into something usable by final form.
 */
const validate = makeValidate(ForgotPasswordSchema, error => {
  return <span className="error">{i18n.t(error.message)}</span>;
});
/**
 * Grabs all the required fields from the schema so that they can be passed into
 * the components without having to declare them in both the schema and the component.
 */
const required = makeRequired(ForgotPasswordSchema);

const ForgotPassword = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AlertStateInterface;
  const { forgotPassword } = useAuth();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  TabTitle(t("forgotPassword.block1.header.page-title"));
  const [submittedValues, setSubmittedValues] = useState<
    ForgotPasswordInterface | undefined
  >(undefined);

  const initialValues: ForgotPasswordInterface = {
    email: ""
  };

  const onSubmit = async (values: ForgotPasswordInterface) => {
    setSubmittedValues(values);

    const captchaToken = recaptchaRef.current.getValue();
 
    recaptchaRef.current.reset();

    if (captchaToken === "" || captchaToken === null){
      navigate("/forgot-password", {
        state: {
          showAlert: true,
          severity: "error",
          message: "forgotPassword.block-form.error.recaptcha"
        }
      });
    } else {
      const submit: any = await forgotPassword(values.email, captchaToken);
      if (submit.status === "FAIL" || submit.error) {
        if  (submit.message === undefined) {
          navigate("/forgot-password", {
            state: {
              showAlert: true,
              severity: "error",
              message: "all.block-form.error.0000"
            }
          });
        } else {
          navigate("/forgot-password", {
            state: {
              showAlert: true,
              severity: "error",
              message: submit.message
            }
          });
        }
      } else {
        navigate("/login", {
          state: {
            showAlert: true,
            severity: "success",
            message:  "forgotPassword.block-form.success"
          }
        });
      }
    }
  };

  return (
    <>
    {state && state.showAlert && (
        <PageAlert
          severity={
            state.severity === "success"
              ? "success"
              : state.severity === "error"
              ? "error"
              : state.severity === "info"
              ? "info"
              : "warning"
          }
        >
          {t(state.message)}
        </PageAlert>
     )}
    <PageBox>
      <Grid container sx={{ pt: 0 }}>
        <Grid item xs={12} sm={6}>
          <PageHeader>
            {t("forgotPassword.block1.header.page-title")}
          </PageHeader>
          <Typography component="p" variant="lead">
            {t("forgotPassword.block1.header.short-intro")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Form
            onSubmit={onSubmit}
            initialValues={
              submittedValues ? submittedValues : initialValues
            }
            validate={validate}
            render={({ submitError, handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                <FormTextField
                  required={required.email}
                  label={t("forgotPassword.block-form.input.email")}
                  id="email"
                  name="email"
                  variant="filled"
                  autoComplete="email"
                  InputProps={{ disableUnderline: true }}
                />
                {submitError && (
                  <FormErrorHelperText
                    variant="filled"
                    filled
                    required
                    component="p"
                  >
                    <span className="error">{t(submitError)}</span>
                  </FormErrorHelperText>
                )}
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_SITE_KEY}
                  size="normal"
                  hl={getI18n().resolvedLanguage}
                />
                <FormWrapperdiv sx={{marginTop: "1.5rem" }}>
                  <FormButton
                    variant="contained"
                    type="submit"
                    disabled={submitting}
                    size="large"
                    sx={{ mr: theme => theme.spacer_m }}
                    disableElevation
                  >
                    {t("forgotPassword.block-form.button.submit")}
                  </FormButton>
                  <PageLink
                    component={Link}
                    to="/login"
                    variant="body1"
                    color="fuchsiaAccessible.main"
                  >
                    {t("all.block-form.button.link")}
                  </PageLink>

                </FormWrapperdiv>
              </form>
            )}
          />
        </Grid>
      </Grid>
    </PageBox>
    </>
  );
};

export default ForgotPassword;
